import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  FileBarChart, Loader2, FileSpreadsheet, FileDown, Printer, Filter,
} from "lucide-react";
import api, { formatApiError } from "../../lib/api";
import { formatEUR } from "../../lib/format";

/**
 * Reports (v4.2.0) — 8 canned operational reports over one shared filter
 * contract. Columns + rows are driven entirely by the backend envelope so
 * the table, CSV, XLSX and print view never drift apart.
 */

const REPORTS = [
  { key: "monthly",           label: "Month / Season / Year" },
  { key: "car_group",         label: "Car Group & Vehicle" },
  { key: "source",            label: "Booking Source" },
  { key: "lead_time",         label: "Lead Time" },
  { key: "utilization",       label: "Utilization & ADR" },
  { key: "station_logistics", label: "Station Logistics" },
  { key: "extras",            label: "Extras Performance" },
  { key: "maintenance",       label: "Maintenance Timeline" },
];

const GRANULARITIES = ["month", "season", "year"];

const isoToday = (offsetDays = 0) => {
  const d = new Date();
  d.setDate(d.getDate() + offsetDays);
  return d.toISOString().slice(0, 10);
};

const fmtCell = (value, kind) => {
  if (value === null || value === undefined || value === "") return "—";
  if (kind === "money") return formatEUR(value);
  if (kind === "pct") return `${value}%`;
  if (kind === "int") return Number(value).toLocaleString();
  return String(value);
};

export default function Reports() {
  const [type, setType] = useState("monthly");
  const [granularity, setGranularity] = useState("month");
  const [from, setFrom] = useState(isoToday(-180));
  const [to, setTo] = useState(isoToday(30));
  const [group, setGroup] = useState("");
  const [source, setSource] = useState("");
  const [vehicles, setVehicles] = useState([]);
  const [sources, setSources] = useState([]);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState("");

  useEffect(() => {
    api.get("/vehicles").then((r) => setVehicles(Array.isArray(r.data) ? r.data : [])).catch(() => {});
    api.get("/admin/settings")
      .then((r) => setSources(r.data?.booking_sources || []))
      .catch(() => setSources(["internet", "direct", "agency", "hotel", "other"]));
  }, []);

  const params = useMemo(
    () => ({ type, from, to, group: group || undefined, source: source || undefined, granularity }),
    [type, from, to, group, source, granularity],
  );

  const load = useCallback(() => {
    setLoading(true);
    api.get("/admin/reports/data", { params })
      .then((r) => setReport(r.data))
      .catch((e) => toast.error(formatApiError(e?.response?.data?.detail) || e.message))
      .finally(() => setLoading(false));
  }, [params]);

  useEffect(() => { load(); }, [load]);

  const download = async (format) => {
    setExporting(format);
    try {
      const res = await api.get("/admin/reports/export", {
        params: { ...params, format },
        responseType: "blob",
      });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `report_${type}_${isoToday()}.${format === "csv" ? "csv" : "xlsx"}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`${format.toUpperCase()} export downloaded`);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || e.message);
    } finally {
      setExporting("");
    }
  };

  const columns = report?.columns || [];

  return (
    <div data-testid="admin-reports-page">
      <style>{`
        @media print {
          body { background: #fff !important; }
          body * { visibility: hidden !important; }
          #report-print-area, #report-print-area * { visibility: visible !important; }
          #report-print-area {
            position: absolute; left: 0; top: 0; width: 100%;
            padding: 0; color: #000; background: #fff;
          }
          .no-print { display: none !important; }
          #report-print-area table { width: 100%; border-collapse: collapse; font-size: 11px; }
          #report-print-area th, #report-print-area td {
            border: 1px solid #ddd; padding: 5px 7px; text-align: left;
          }
          #report-print-area thead th { background: #f3f3f3 !important; }
          @page { size: A4 landscape; margin: 12mm; }
        }
      `}</style>

      <div className="flex flex-wrap items-end justify-between gap-4 mb-8 no-print">
        <div>
          <p className="text-[10px] tracking-[0.3em] uppercase text-neutral-500 dark:text-neutral-400 inline-flex items-center gap-2">
            <FileBarChart size={13} /> Business intelligence
          </p>
          <h1 className="font-display text-4xl md:text-5xl tracking-tight mt-2">
            Reports &amp; <em className="italic">exports</em>.
          </h1>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button
            onClick={() => download("xls")}
            disabled={!!exporting}
            className="inline-flex items-center gap-2 border border-black/15 dark:border-white/20 px-4 py-2.5 text-sm rounded hover:border-black dark:hover:border-white disabled:opacity-50"
            data-testid="reports-export-xls"
          >
            {exporting === "xls" ? <Loader2 size={14} className="animate-spin" /> : <FileSpreadsheet size={14} />} Export XLS
          </button>
          <button
            onClick={() => download("csv")}
            disabled={!!exporting}
            className="inline-flex items-center gap-2 border border-black/15 dark:border-white/20 px-4 py-2.5 text-sm rounded hover:border-black dark:hover:border-white disabled:opacity-50"
            data-testid="reports-export-csv"
          >
            {exporting === "csv" ? <Loader2 size={14} className="animate-spin" /> : <FileDown size={14} />} Export CSV
          </button>
          <button
            onClick={() => window.print()}
            className="inline-flex items-center gap-2 bg-black text-white dark:bg-white dark:text-black px-4 py-2.5 text-sm rounded hover:opacity-90"
            data-testid="reports-export-pdf"
          >
            <Printer size={14} /> Export PDF
          </button>
        </div>
      </div>

      {/* ── Filters ─────────────────────────────────────────────────── */}
      <div
        className="border border-black/10 dark:border-white/10 rounded-xl p-5 mb-6 no-print"
        data-testid="reports-filters"
      >
        <p className="text-[10px] tracking-[0.25em] uppercase text-neutral-500 dark:text-neutral-400 inline-flex items-center gap-1.5 mb-4">
          <Filter size={12} /> Filters
        </p>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Field label="From">
            <input type="date" value={from} onChange={(e) => setFrom(e.target.value)}
              className="rep-input" data-testid="reports-from" />
          </Field>
          <Field label="To">
            <input type="date" value={to} onChange={(e) => setTo(e.target.value)}
              className="rep-input" data-testid="reports-to" />
          </Field>
          <Field label="Car group">
            <select value={group} onChange={(e) => setGroup(e.target.value)}
              className="rep-input" data-testid="reports-group">
              <option value="">All car groups</option>
              {vehicles.map((v) => (
                <option key={v.id} value={v.id}>{v.brand} {v.name}</option>
              ))}
            </select>
          </Field>
          <Field label="Booking source">
            <select value={source} onChange={(e) => setSource(e.target.value)}
              className="rep-input" data-testid="reports-source">
              <option value="">All sources</option>
              {sources.map((s) => (
                <option key={s} value={s}>{s.replace(/_/g, " ")}</option>
              ))}
            </select>
          </Field>
        </div>
        {type === "monthly" && (
          <div className="flex gap-2 mt-4">
            {GRANULARITIES.map((g) => (
              <button
                key={g}
                onClick={() => setGranularity(g)}
                className={`px-3.5 py-1.5 text-xs rounded-full border capitalize transition-colors ${
                  granularity === g
                    ? "bg-black text-white border-black dark:bg-white dark:text-black"
                    : "border-black/15 dark:border-white/20 text-neutral-600 dark:text-neutral-300"
                }`}
                data-testid={`reports-granularity-${g}`}
              >
                By {g}
              </button>
            ))}
          </div>
        )}
        <style>{`
          .rep-input {
            width: 100%; background: transparent; border: 1px solid rgba(0,0,0,.15);
            border-radius: 6px; padding: 9px 10px; font-size: 14px; outline: none;
          }
          .dark .rep-input { border-color: rgba(255,255,255,.2); color: #fff; }
          .dark .rep-input option { color: #111; }
          .rep-input:focus { border-color: #111; }
          .dark .rep-input:focus { border-color: #fff; }
        `}</style>
      </div>

      {/* ── Report tabs ─────────────────────────────────────────────── */}
      <div className="flex gap-1 border-b border-black/10 dark:border-white/10 mb-6 overflow-x-auto no-print">
        {REPORTS.map((r) => (
          <button
            key={r.key}
            onClick={() => setType(r.key)}
            className={`px-4 py-2.5 text-sm whitespace-nowrap border-b-2 -mb-px transition-colors ${
              type === r.key
                ? "border-black dark:border-white font-semibold"
                : "border-transparent text-neutral-500 hover:text-black dark:hover:text-white"
            }`}
            data-testid={`reports-tab-${r.key}`}
          >
            {r.label}
          </button>
        ))}
      </div>

      {/* ── Printable area ──────────────────────────────────────────── */}
      <div id="report-print-area">
        <div className="mb-5">
          <h2 className="font-display text-2xl" data-testid="reports-title">{report?.title || "Report"}</h2>
          <p className="text-sm text-neutral-500 mt-1" data-testid="reports-subtitle">
            {from || "all"} → {to || "all"}
            {group ? ` · ${vehicles.find((v) => v.id === group)?.name || "group"}` : " · all car groups"}
            {source ? ` · ${source}` : " · all sources"}
            {report ? ` · ${report.row_count} row(s)` : ""}
          </p>
        </div>

        {report?.meta && Object.keys(report.meta).length > 0 && (
          <div className="flex flex-wrap gap-4 mb-5" data-testid="reports-meta">
            {Object.entries(report.meta).map(([k, v]) => (
              <div key={k} className="border border-black/10 dark:border-white/10 rounded-lg px-4 py-3">
                <p className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 capitalize">
                  {k.replace(/_/g, " ")}
                </p>
                <p className="font-display text-xl tabular-nums mt-1">
                  {typeof v === "number" ? v.toLocaleString() : String(v)}
                </p>
              </div>
            ))}
          </div>
        )}

        {loading ? (
          <div className="py-24 flex justify-center" data-testid="reports-loading">
            <Loader2 className="animate-spin text-neutral-400" />
          </div>
        ) : (
          <div className="border border-black/10 dark:border-white/10 rounded-xl overflow-x-auto">
            <table className="w-full text-sm" data-testid="reports-table">
              <thead>
                <tr className="bg-neutral-50 dark:bg-white/5 text-left">
                  {columns.map((c) => (
                    <th key={c.key} className="px-4 py-3 text-[10px] uppercase tracking-[0.2em] text-neutral-500 font-semibold whitespace-nowrap">
                      {c.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(report?.rows || []).length === 0 ? (
                  <tr>
                    <td colSpan={columns.length || 1} className="px-4 py-12 text-center text-neutral-500" data-testid="reports-empty">
                      No data for the selected filters.
                    </td>
                  </tr>
                ) : (
                  (report?.rows || []).map((row, i) => (
                    <tr key={i} className="border-t border-black/5 dark:border-white/5" data-testid={`reports-row-${i}`}>
                      {columns.map((c) => (
                        <td key={c.key} className="px-4 py-3 whitespace-nowrap tabular-nums">
                          {fmtCell(row[c.key], c.kind)}
                        </td>
                      ))}
                    </tr>
                  ))
                )}
              </tbody>
              {report?.totals && Object.keys(report.totals).length > 0 && (report?.rows || []).length > 0 && (
                <tfoot>
                  <tr className="border-t-2 border-black/15 dark:border-white/20 font-semibold bg-neutral-50 dark:bg-white/5" data-testid="reports-totals">
                    {columns.map((c, idx) => (
                      <td key={c.key} className="px-4 py-3 whitespace-nowrap tabular-nums">
                        {idx === 0
                          ? "Total"
                          : report.totals[c.key] !== undefined
                            ? fmtCell(report.totals[c.key], c.kind)
                            : ""}
                      </td>
                    ))}
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-[10px] uppercase tracking-[0.2em] text-neutral-500 dark:text-neutral-400 mb-1.5">
        {label}
      </span>
      {children}
    </label>
  );
}
